/**

 * ServerMonitoringDashboard.jsx — Unified eDC shell (Operations + Infrastructure tabs)

 */

import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';

import { createPortal } from 'react-dom';

import { useLocation, useNavigate } from 'react-router-dom';

import GridLayout from 'react-grid-layout';

import 'react-grid-layout/css/styles.css';

import 'react-resizable/css/styles.css';

import {

  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,

  XAxis, YAxis, Tooltip, Brush,

} from 'recharts';

import {

  FiX, FiRefreshCw, FiPause, FiPlay, FiCpu, FiHardDrive, FiActivity, FiAlertTriangle,

  FiBarChart2, FiTrendingUp, FiLayers, FiSearch, FiCheck, FiChevronUp, FiChevronDown, FiRotateCcw, FiTrash2, FiCalendar, FiDownload, FiServer, FiMaximize2

} from 'react-icons/fi';

import {

  fetchServers, fetchStatusLogs, fetchStatusLogHistory, fetchMetricStats, fetchSyncReports, fetchLogServersSnapshot, getBase

} from './serverApi';

import { DeleteServerModal, TruncateLogModal } from './ServerModals';

import InfrastructureDashboard from './InfrastructureDashboard';



// ─── Config ───



/**

 * Default chart widgets — only metrics that map to real server_status_log fields.

 */

const METRIC_DEFS = {

  cpu: {

    id: 'cpu',

    title: 'CPU Utilization',

    dataKey: 'CPULOAD',

    unit: '%',

    defaultType: 'area',

    color: '#dc2626',

    fill: 'rgba(220,38,38,0.12)',

  },

  memory: {

    id: 'memory',

    title: 'Memory Usage',

    dataKey: 'MEMORYLOAD',

    unit: '%',

    defaultType: 'area',

    color: '#6b7280',

    fill: 'rgba(107,114,128,0.15)',

  },

  disk: {

    id: 'disk',

    title: 'Disk Size',

    dataKey: 'DISKSIZE',

    unit: ' GB',

    defaultType: 'line',

    color: '#dc2626',

    fill: 'rgba(220,38,38,0.08)',

  },

  processes: {

    id: 'processes',

    title: 'Running Processes',

    dataKey: 'PROCESSES',

    unit: '',

    defaultType: 'bar',

    color: '#991b1b',

    fill: 'rgba(153,27,27,0.2)',

  },

  latency: {

    id: 'latency',

    title: 'Request Latency',

    dataKey: 'REQUEST_LATENCY_MS',

    unit: ' ms',

    defaultType: 'line',

    color: '#dc2626',

    fill: 'rgba(220,38,38,0.1)',

    derived: true,

  },

};



const DEFAULT_WIDGETS = Object.values(METRIC_DEFS).map((m) => ({

  id: m.id,

  metricId: m.id,

  chartType: m.defaultType,

}));



/** h=2 @ rowHeight 88 ≈ 188px — fits header + chart without bottom gap */

const GRID_ROW_HEIGHT = 88;

const DEFAULT_LAYOUT = [

  { i: 'cpu', x: 0, y: 0, w: 4, h: 2, minW: 3, minH: 2 },

  { i: 'memory', x: 4, y: 0, w: 4, h: 2, minW: 3, minH: 2 },

  { i: 'latency', x: 8, y: 0, w: 4, h: 2, minW: 3, minH: 2 },

  { i: 'disk', x: 0, y: 2, w: 6, h: 2, minW: 3, minH: 2 },

  { i: 'processes', x: 6, y: 2, w: 6, h: 2, minW: 3, minH: 2 },

];



const LAYOUT_STORAGE_KEY = 'server_monitoring_layout_v4';

const WIDGETS_STORAGE_KEY = 'server_monitoring_widgets_v2';

const UNDO_STACK_SESSION_KEY = 'server_monitoring_undo_v1';



const QUICK_PROMPTS = [

  'Summarize dashboard health',

  'Any anomalies right now?',

  'Why is CPU high?',

  'Add a chart for request latency',

];



const POLL_MS = 5000;

/** Demo mode: refresh from log table (less aggressive) */

const DEMO_POLL_MS = 30000;

const DATA_MODE_KEY = 'server_monitoring_data_mode';

const DATA_MODES = {

  demo: { id: 'demo', label: 'Demo', hint: 'Charts built from status log table rows' },

  live: { id: 'live', label: 'Live', hint: 'Production stream (aggregated server metrics)' },

};





// ─── Utils & log series ───



/**

 * Parsing, health, and trend helpers for the monitoring dashboard.

 */



function parseMetricValue(raw) {

  if (raw == null || raw === '') return null;

  const n = parseFloat(String(raw).replace(/[^\d.-]/g, ''));

  return Number.isFinite(n) ? n : null;

}



function getServerLoad(server, logByName) {

  const log = logByName?.[server.server_name];

  if (!log) {

    return { cpu: 0, mem: 0, processes: 0, hasLog: false };

  }

  return {

    cpu: parseMetricValue(log.CPULOAD) ?? 0,

    mem: parseMetricValue(log.MEMORYLOAD) ?? 0,

    processes: parseInt(log.PROCESSES, 10) || 0,

    hasLog: true,

  };

}



/** Inventory JSON servers plus status-log-only servers (e.g. kascportal). */

function mergeInventoryWithLogServers(inventoryServers, logByName) {

  const merged = new Map();

  inventoryServers.forEach((server) => {

    const name = server.server_name;

    if (name) merged.set(name.toLowerCase(), server);

  });

  Object.keys(logByName || {}).forEach((name) => {

    const key = name.toLowerCase();

    if (merged.has(key)) return;

    merged.set(key, {

      id: `log-${name}`,

      server_name: name,

      os_version: '',

      private_ip: '',

      status: 1,

      logOnly: true,

    });

  });

  return Array.from(merged.values());

}



function healthFromLoad(cpu, mem, status, hasLog = true) {

  if (status === 0 || status === '0') {

    return { label: 'Offline', tone: 'offline', pct: 0 };

  }

  if (!hasLog) {

    return { label: 'No logs', tone: 'offline', pct: 0 };

  }

  const score = Math.max(cpu, mem);

  if (score >= 90) return { label: 'Critical', tone: 'critical', pct: score };

  if (score >= 70) return { label: 'Warning', tone: 'warning', pct: score };

  return { label: 'Healthy', tone: 'healthy', pct: score };

}



const TONE_STYLES = {

  healthy: { badge: 'bg-emerald-100 text-emerald-800', bar: 'bg-emerald-500', border: 'border-emerald-200' },

  warning: { badge: 'bg-amber-100 text-amber-800', bar: 'bg-amber-500', border: 'border-amber-300' },

  critical: { badge: 'bg-red-100 text-red-800', bar: 'bg-red-600', border: 'border-red-400 ring-1 ring-red-200' },

  offline: { badge: 'bg-gray-100 text-gray-600', bar: 'bg-gray-400', border: 'border-gray-200' },

};



function toneStyle(tone) {

  return TONE_STYLES[tone] || TONE_STYLES.offline;

}



/** Compare last N points vs previous N for trend % */

function trendPercent(history, dataKey, window = 5) {

  if (!history?.length || history.length < window * 2) return null;

  const vals = history.map((h) => parseFloat(h[dataKey])).filter((v) => Number.isFinite(v));

  if (vals.length < window * 2) return null;

  const recent = vals.slice(-window);

  const prior = vals.slice(-window * 2, -window);

  const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;

  const r = avg(recent);

  const p = avg(prior);

  if (p === 0) return r > 0 ? 100 : 0;

  return ((r - p) / p) * 100;

}



function computeMetricStats(history, dataKey) {

  if (!history?.length) return null;

  const vals = history

    .map((h) => parseFloat(h[dataKey]))

    .filter((v) => Number.isFinite(v));

  if (!vals.length) return null;

  const sum = vals.reduce((a, b) => a + b, 0);

  return {

    current: vals[vals.length - 1],

    min: Math.min(...vals),

    max: Math.max(...vals),

    avg: sum / vals.length,

    trend: trendPercent(history, dataKey),

    samples: vals.length,

    firstDate: history[0]?.Date || '—',

    lastDate: history[history.length - 1]?.Date || '—',

  };

}



function aggregateKpis(history, syncFailed = 0) {

  const last = history[history.length - 1];

  const cpu = last ? parseFloat(last.CPULOAD) : 0;

  const mem = last ? parseFloat(last.MEMORYLOAD) : 0;

  const proc = last ? parseInt(last.PROCESSES, 10) || 0 : 0;

  const lat = last ? parseFloat(last.REQUEST_LATENCY_MS) : 0;

  return {

    cpu: { value: cpu, trend: trendPercent(history, 'CPULOAD') },

    memory: { value: mem, trend: trendPercent(history, 'MEMORYLOAD') },

    processes: { value: proc, trend: trendPercent(history, 'PROCESSES') },

    latency: { value: lat, trend: trendPercent(history, 'REQUEST_LATENCY_MS') },

    errors: { value: syncFailed, trend: null },

  };

}



function latestPoint(history, dataKey) {

  if (!history?.length) return null;

  const last = history[history.length - 1];

  const v = parseFloat(last[dataKey]);

  return Number.isFinite(v) ? v : null;

}



function historySignature(history) {

  if (!history?.length) return '';

  const tail = history.slice(-3);

  return JSON.stringify(tail);

}



/**

 * Build time-series for charts from server_status_log rows (same source as the table).

 */



function normalizeLogRow(item) {

  if (Array.isArray(item)) {

    return {

      SERVERNAME: item[1] || '',

      TYPE: item[2] || 'SERVER',

      CPULOAD: parseFloat(item[3]) || 0,

      MEMORYLOAD: parseFloat(item[4]) || 0,

      PROCESSES: parseInt(item[5], 10) || 0,

      DISKSIZE: parseFloat(item[6]) || 0,

      REPORTINGDATETIME: item[7] || '',

    };

  }

  return {

    SERVERNAME: item.SERVERNAME || '',

    TYPE: item.TYPE || 'SERVER',

    CPULOAD: parseFloat(item.CPULOAD) || 0,

    MEMORYLOAD: parseFloat(item.MEMORYLOAD) || 0,

    PROCESSES: parseInt(item.PROCESSES, 10) || 0,

    DISKSIZE: parseFloat(item.DISKSIZE) || 0,

    REPORTINGDATETIME: item.REPORTINGDATETIME || '',

  };

}



/**

 * Estimated request latency (ms) from log load metrics — correlates with CPU/memory/process pressure.

 */

function computeRequestLatencyMs(row) {

  const cpu = parseFloat(row.CPULOAD) || 0;

  const mem = parseFloat(row.MEMORYLOAD) || 0;

  const proc = parseInt(row.PROCESSES, 10) || 0;

  const procFactor = Math.min(proc / 400, 1) * 40;

  const ms = cpu * 0.55 + mem * 0.35 + procFactor;

  return Math.round(Math.min(500, Math.max(0, ms)) * 10) / 10;

}



function formatChartDate(dt) {

  if (!dt) return '';

  const s = String(dt);

  const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));

  if (!Number.isNaN(d.getTime())) {

    return d.toLocaleString('en-GB', {

      day: '2-digit', month: '2-digit', year: '2-digit',

      hour: '2-digit', minute: '2-digit', hour12: true,

    });

  }

  return s.length > 16 ? s.slice(0, 16) : s;

}



/**

 * Aggregate log rows by reporting time (newest last for charts).

 */

function buildChartSeriesFromLogs(rawRows, maxPoints = 400) {

  if (!Array.isArray(rawRows) || rawRows.length === 0) return [];



  const rows = rawRows.map(normalizeLogRow).filter((r) => r.REPORTINGDATETIME);

  const byTime = new Map();



  rows.forEach((r) => {

    const key = r.REPORTINGDATETIME;

    const lat = computeRequestLatencyMs(r);

    if (!byTime.has(key)) {

      byTime.set(key, {

        _ts: key,

        Date: formatChartDate(key),

        CPULOAD: r.CPULOAD,

        MEMORYLOAD: r.MEMORYLOAD,

        PROCESSES: r.PROCESSES,

        DISKSIZE: r.DISKSIZE,

        REQUEST_LATENCY_MS: lat,

        _n: 1,

      });

      return;

    }

    const bucket = byTime.get(key);

    const n = bucket._n + 1;

    bucket.CPULOAD = (bucket.CPULOAD * bucket._n + r.CPULOAD) / n;

    bucket.MEMORYLOAD = (bucket.MEMORYLOAD * bucket._n + r.MEMORYLOAD) / n;

    bucket.PROCESSES = (bucket.PROCESSES * bucket._n + r.PROCESSES) / n;

    bucket.DISKSIZE = (bucket.DISKSIZE * bucket._n + r.DISKSIZE) / n;

    bucket.REQUEST_LATENCY_MS = (bucket.REQUEST_LATENCY_MS * bucket._n + lat) / n;

    bucket._n = n;

  });



  const sorted = [...byTime.values()]

    .sort((a, b) => String(a._ts).localeCompare(String(b._ts)))

    .slice(-maxPoints)

    .map(({ _ts, _n, ...point }) => ({

      ...point,

      CPULOAD: Math.round(point.CPULOAD * 10) / 10,

      MEMORYLOAD: Math.round(point.MEMORYLOAD * 10) / 10,

      PROCESSES: Math.round(point.PROCESSES),

      DISKSIZE: Math.round(point.DISKSIZE * 10) / 10,

      REQUEST_LATENCY_MS: Math.round(point.REQUEST_LATENCY_MS * 10) / 10,

    }));



  return sorted;

}





// ─── Hooks ───



/**

 * Measure chart area inside a grid widget (fills cell minus header).

 */

function useChartSize(containerRef, headerRef, minHeight = 100) {

  const [size, setSize] = useState({ width: 280, height: minHeight });



  useEffect(() => {

    const container = containerRef.current;

    const header = headerRef.current;

    if (!container) return undefined;



    const measure = () => {

      const w = Math.max(180, Math.floor(container.clientWidth));

      const headerH = header?.offsetHeight ?? 52;

      const h = Math.max(minHeight, Math.floor(container.clientHeight - headerH));

      setSize({ width: w, height: h });

    };



    measure();

    const ro = new ResizeObserver(measure);

    ro.observe(container);

    if (header) ro.observe(header);

    return () => ro.disconnect();

  }, [containerRef, headerRef, minHeight]);



  return size;

}





const LOG_FETCH_SIZE = 500;



function enrichHistoryPoints(rows) {

  if (!Array.isArray(rows)) return [];

  return rows.map((row) => ({

    ...row,

    CPULOAD: parseFloat(row.CPULOAD) || 0,

    MEMORYLOAD: parseFloat(row.MEMORYLOAD) || 0,

    PROCESSES: parseInt(row.PROCESSES, 10) || 0,

    DISKSIZE: parseFloat(row.DISKSIZE) || 0,

    REQUEST_LATENCY_MS: row.REQUEST_LATENCY_MS ?? computeRequestLatencyMs(row),

  }));

}



function parseHistoryResponse(hRes) {

  let historyData = hRes.data;

  if (typeof historyData === 'string') {

    try {

      historyData = JSON.parse(historyData);

    } catch {

      const start = historyData.indexOf('[');

      const end = historyData.lastIndexOf(']');

      if (start !== -1 && end !== -1) {

        historyData = JSON.parse(historyData.substring(start, end + 1));

      }

    }

  }

  return Array.isArray(historyData) ? enrichHistoryPoints(historyData) : [];

}



/**

 * Demo: build chart series from status log table rows (same API as the table).

 * Live: production stream via get_server_log_history (aggregated time buckets).

 */

function useMetricsData(selectedServer, liveEnabled, dataMode = 'demo') {

  const [servers, setServers] = useState([]);

  const [history, setHistory] = useState([]);

  const [syncFailed, setSyncFailed] = useState(0);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [lastUpdated, setLastUpdated] = useState(null);

  const sigRef = useRef('');

  const mountedRef = useRef(true);



  const isDemo = dataMode === 'demo';



  const load = useCallback(async (showSpinner = false) => {

    if (showSpinner) setLoading(true);

    try {

      const [sRes, syncRes] = await Promise.all([

        fetchServers('', 0, 200),

        fetchSyncReports().catch(() => ({ data: null })),

      ]);



      let series = [];



      if (isDemo) {

        const logRes = await fetchStatusLogs(selectedServer, 0, LOG_FETCH_SIZE);

        if (logRes.data?.data) {

          series = buildChartSeriesFromLogs(logRes.data.data, LOG_FETCH_SIZE);

        }

      } else {

        const hRes = await fetchStatusLogHistory(selectedServer);

        series = parseHistoryResponse(hRes);

        if (series.length === 0) {

          const logRes = await fetchStatusLogs(selectedServer, 0, LOG_FETCH_SIZE);

          if (logRes.data?.data) {

            series = buildChartSeriesFromLogs(logRes.data.data, LOG_FETCH_SIZE);

          }

        }

      }



      if (!mountedRef.current) return;



      if (sRes.data?.data) setServers(sRes.data.data);



      const sig = historySignature(series);

      if (sig !== sigRef.current) {

        sigRef.current = sig;

        setHistory(series);

      }



      if (syncRes?.data?.status && Array.isArray(syncRes.data.data)) {

        setSyncFailed(syncRes.data.data.filter((r) => r.status !== 'Success').length);

      }



      setLastUpdated(new Date());

      setError(null);

    } catch (e) {

      if (mountedRef.current) setError(e?.message || 'Failed to load metrics');

    } finally {

      if (mountedRef.current) setLoading(false);

    }

  }, [selectedServer, isDemo]);



  useEffect(() => {

    mountedRef.current = true;

    sigRef.current = '';

    load(true);

    return () => { mountedRef.current = false; };

  }, [load]);



  useEffect(() => {

    if (!liveEnabled) return undefined;

    const ms = isDemo ? DEMO_POLL_MS : POLL_MS;

    const id = setInterval(() => load(false), ms);

    return () => clearInterval(id);

  }, [liveEnabled, load, isDemo]);



  return {

    servers,

    history,

    syncFailed,

    loading,

    error,

    lastUpdated,

    refresh: () => load(true),

    dataMode,

    isDemo,

    sourceLabel: isDemo

      ? 'Demo · status log table'

      : 'Live · production stream',

  };

}





// ─── Subcomponents ───





const TYPE_ICONS = { area: FiLayers, line: FiTrendingUp, bar: FiBarChart2 };



function shortDateLabel(value) {

  const s = String(value || '');

  if (s.length <= 14) return s;

  return s.replace(/,\s*\d{4}/, '').trim();

}



const AXIS_PROPS = {

  tick: { fontSize: 8, fill: '#9ca3af' },

  interval: 'preserveStartEnd',

  height: 22,

  tickMargin: 2,

  tickFormatter: shortDateLabel,

  axisLine: false,

  tickLine: false,

};



const Y_AXIS_PROPS = {

  tick: { fontSize: 9, fill: '#9ca3af' },

  width: 36,

  domain: ['auto', 'auto'],

  axisLine: false,

  tickLine: false,

};



const HISTORY_Y_AXIS_PROPS = {

  tick: { fontSize: 11, fill: '#6b7280' },

  axisLine: false,

  tickLine: false,

};



const CHART_TOOLTIP_PROPS = {

  contentStyle: { fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' },

  cursor: false,

};



const HISTORY_TOOLTIP_PROPS = {

  contentStyle: { fontSize: 12, borderRadius: 8 },

  cursor: false,

};



function sanitizeFilenamePart(value) {

  return String(value || '')

    .trim()

    .replace(/[^\w.-]+/g, '_')

    .replace(/^_+|_+$/g, '');

}



function buildChartFilename(serverName, ...parts) {

  const segments = [serverName, ...parts].map(sanitizeFilenamePart).filter(Boolean);

  return `${segments.join('-') || 'chart'}.png`;

}



function useFullscreenChartSize(active, paddingX = 80, paddingY = 110) {

  const [size, setSize] = useState({ width: 960, height: 540 });



  useEffect(() => {

    if (!active) return undefined;

    const update = () => {

      setSize({

        width: Math.max(480, window.innerWidth - paddingX),

        height: Math.max(320, window.innerHeight - paddingY),

      });

    };

    update();

    window.addEventListener('resize', update);

    return () => window.removeEventListener('resize', update);

  }, [active, paddingX, paddingY]);



  return size;

}



function ChartFullscreenModal({ open, title, onClose, children }) {

  useEffect(() => {

    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    const onKey = (e) => {

      if (e.key === 'Escape') onClose();

    };

    window.addEventListener('keydown', onKey);

    return () => {

      document.body.style.overflow = previousOverflow;

      window.removeEventListener('keydown', onKey);

    };

  }, [open, onClose]);



  if (!open) return null;



  return createPortal(

    <div className="fixed inset-0 z-[300] bg-white flex flex-col">

      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 shrink-0">

        <h3 className="text-base font-bold text-gray-900 truncate pr-4">{title}</h3>

        <button

          type="button"

          onClick={onClose}

          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition"

          title="Close (Esc)"

        >

          <FiX className="w-5 h-5" />

        </button>

      </div>

      <div className="flex-1 min-h-0 w-full flex items-center justify-center p-4 sm:p-8">

        <div className="w-full h-full max-w-[1400px] mx-auto flex items-center justify-center">

          {children}

        </div>

      </div>

    </div>,

    document.body,

  );

}



function downloadChartImage(containerEl, filename = 'chart.png') {

  const svg = containerEl?.querySelector('.recharts-surface') || containerEl?.querySelector('svg');

  if (!svg) return;

  const rect = svg.getBoundingClientRect();

  const width = Math.max(Math.round(rect.width), 320);

  const height = Math.max(Math.round(rect.height), 180);

  const clone = svg.cloneNode(true);

  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  clone.setAttribute('width', String(width));

  clone.setAttribute('height', String(height));

  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');

  bg.setAttribute('width', '100%');

  bg.setAttribute('height', '100%');

  bg.setAttribute('fill', '#ffffff');

  clone.insertBefore(bg, clone.firstChild);

  const svgString = new XMLSerializer().serializeToString(clone);

  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });

  const url = URL.createObjectURL(svgBlob);

  const img = new Image();

  img.onload = () => {

    const canvas = document.createElement('canvas');

    canvas.width = width * 2;

    canvas.height = height * 2;

    const ctx = canvas.getContext('2d');

    ctx.scale(2, 2);

    ctx.fillStyle = '#ffffff';

    ctx.fillRect(0, 0, width, height);

    ctx.drawImage(img, 0, 0, width, height);

    canvas.toBlob((blob) => {

      if (!blob) return;

      const link = document.createElement('a');

      link.href = URL.createObjectURL(blob);

      link.download = filename;

      link.click();

      URL.revokeObjectURL(link.href);

    }, 'image/png');

    URL.revokeObjectURL(url);

  };

  img.src = url;

}



function ChartExportActions({ containerRef, filename, onFullscreen, className = '' }) {

  return (

    <div className={`flex items-center gap-0.5 flex-shrink-0 ${className}`}>

      <button

        type="button"

        title="Download chart image"

        onClick={() => downloadChartImage(containerRef.current, filename)}

        className="p-1 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition"

      >

        <FiDownload className="w-3.5 h-3.5" />

      </button>

      <button

        type="button"

        title="Full screen"

        onClick={() => onFullscreen?.()}

        className="p-1 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition"

      >

        <FiMaximize2 className="w-3.5 h-3.5" />

      </button>

    </div>

  );

}



function ChartBody({ data, metric, chartType, width, height }) {

  const { dataKey, color } = metric;

  const showDot = data.length <= 3;

  const plotH = Math.max(80, height);

  const common = {

    data,

    width,

    height: plotH,

    margin: { top: 6, right: 8, left: 0, bottom: 0 },

  };



  if (chartType === 'bar') {

    return (

      <BarChart {...common}>

        <XAxis dataKey="Date" {...AXIS_PROPS} />

        <YAxis {...Y_AXIS_PROPS} />

        <Tooltip {...CHART_TOOLTIP_PROPS} />

        <Bar dataKey={dataKey} fill={color} radius={[2, 2, 0, 0]} maxBarSize={14} isAnimationActive={false} />

      </BarChart>

    );

  }



  if (chartType === 'line') {

    return (

      <LineChart {...common}>

        <XAxis dataKey="Date" {...AXIS_PROPS} />

        <YAxis {...Y_AXIS_PROPS} />

        <Tooltip {...CHART_TOOLTIP_PROPS} />

        <Line

          type="monotone"

          dataKey={dataKey}

          stroke={color}

          strokeWidth={2}

          dot={showDot}

          isAnimationActive={false}

          connectNulls

        />

      </LineChart>

    );

  }



  return (

    <AreaChart {...common}>

      <defs>

        <linearGradient id={`grad-${metric.id}`} x1="0" y1="0" x2="0" y2="1">

          <stop offset="0%" stopColor={color} stopOpacity={0.25} />

          <stop offset="100%" stopColor={color} stopOpacity={0} />

        </linearGradient>

      </defs>

      <XAxis dataKey="Date" {...AXIS_PROPS} />

      <YAxis {...Y_AXIS_PROPS} />

      <Tooltip {...CHART_TOOLTIP_PROPS} />

      <Area

        type="monotone"

        dataKey={dataKey}

        stroke={color}

        fill={`url(#grad-${metric.id})`}

        strokeWidth={2}

        dot={showDot}

        isAnimationActive={false}

        connectNulls

      />

    </AreaChart>

  );

}



function DashboardChartWidget({

  widgetId,

  metricId,

  chartType,

  history,

  selectedServer = '',

  onTypeChange,

  onRemove,

  isPreview,

}) {

  const rootRef = useRef(null);

  const headerRef = useRef(null);

  const chartRef = useRef(null);

  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const fullscreenSize = useFullscreenChartSize(fullscreenOpen);

  const { width, height } = useChartSize(rootRef, headerRef, 90);



  const metric = METRIC_DEFS[metricId];

  if (!metric) return null;



  const latest = latestPoint(history, metric.dataKey);

  let displayVal = latest;

  if (displayVal != null && metric.unit === '%') displayVal = Math.min(100, displayVal);

  const display =

    displayVal != null

      ? `${displayVal.toFixed(metric.unit === ' GB' ? 1 : 1)}${metric.unit}`

      : '—';



  const types = ['area', 'line', 'bar'];

  const hasChart = history.length > 0;

  const serverLabel = selectedServer || 'all-servers';

  const chartTitle = selectedServer ? `${selectedServer} - ${metric.title}` : metric.title;

  const downloadFilename = buildChartFilename(serverLabel, metric.id);



  return (

    <>

    <div

      ref={rootRef}

      className={`h-full flex flex-col bg-white rounded-xl border shadow-sm overflow-hidden

        ${isPreview ? 'border-amber-300 ring-2 ring-amber-100' : 'border-gray-100'}`}

    >

      <div

        ref={headerRef}

        className="drag-handle cursor-grab active:cursor-grabbing px-3 py-2 border-b border-gray-50 flex items-start justify-between gap-2 flex-shrink-0"

      >

        <div className="min-w-0">

          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide truncate leading-tight">

            {metric.title}

          </p>

          <p className="text-base font-bold text-gray-900 leading-tight">{display}</p>

        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0">

          <ChartExportActions

            containerRef={chartRef}

            filename={downloadFilename}

            onFullscreen={() => setFullscreenOpen(true)}

          />

          {types.map((t) => {

            const Icon = TYPE_ICONS[t];

            return (

              <button

                key={t}

                type="button"

                title={`${t} chart`}

                onClick={() => onTypeChange(widgetId, t)}

                className={`p-1 rounded-md transition ${

                  chartType === t ? 'bg-red-50 text-red-700' : 'text-gray-400 hover:bg-gray-100'

                }`}

              >

                <Icon className="w-3.5 h-3.5" />

              </button>

            );

          })}

          <button

            type="button"

            onClick={() => onRemove(widgetId)}

            className="p-1 rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600 transition"

            title="Remove chart"

          >

            <FiX className="w-3.5 h-3.5" />

          </button>

        </div>

      </div>

      <div

        ref={chartRef}

        className="flex-1 min-h-0 w-full px-0.5 pb-0.5 overflow-hidden bg-white outline-none"

        onMouseDown={(e) => e.stopPropagation()}

        onPointerDown={(e) => e.stopPropagation()}

      >

        {hasChart ? (

          <ChartBody

            data={history}

            metric={metric}

            chartType={chartType}

            width={width}

            height={height}

          />

        ) : (

          <div className="h-full flex items-center justify-center text-xs text-gray-400 text-center px-2">

            No log rows yet — add data in the status table below

          </div>

        )}

      </div>

    </div>



    <ChartFullscreenModal

      open={fullscreenOpen}

      title={chartTitle}

      onClose={() => setFullscreenOpen(false)}

    >

      {hasChart && (

        <ChartBody

          data={history}

          metric={metric}

          chartType={chartType}

          width={fullscreenSize.width}

          height={fullscreenSize.height}

        />

      )}

    </ChartFullscreenModal>

    </>

  );

}







const SERIES = [

  { key: 'CPULOAD', name: 'CPULOAD', color: '#2563eb' },

  { key: 'MEMORYLOAD', name: 'MEMORYLOAD', color: '#eab308' },

  { key: 'PROCESSES', name: 'PROCESSES', color: '#16a34a' },

  { key: 'DISKSIZE', name: 'DISKSIZE', color: '#dc2626' },

  { key: 'REQUEST_LATENCY_MS', name: 'Request Latency (ms)', color: '#7c3aed' },

];



const SERIES_META = {

  CPULOAD: {

    title: 'CPU Load',

    unit: '%',

    description: 'Processor utilization reported from server status logs.',

  },

  MEMORYLOAD: {

    title: 'Memory Load',

    unit: '%',

    description: 'Memory pressure across the selected reporting window.',

  },

  PROCESSES: {

    title: 'Running Processes',

    unit: '',

    description: 'Count of active processes at each sample time.',

  },

  DISKSIZE: {

    title: 'Disk Size',

    unit: ' GB',

    description: 'Aggregated disk usage in gigabytes.',

  },

  REQUEST_LATENCY_MS: {

    title: 'Request Latency',

    unit: ' ms',

    description: 'Estimated response time derived from CPU, memory, and process load.',

    derived: true,

  },

};



function formatMetricValue(value, unit) {

  if (!Number.isFinite(value)) return '—';

  const decimals = unit === ' GB' ? 1 : unit === ' ms' ? 1 : unit === '%' ? 1 : 0;

  return `${value.toFixed(decimals)}${unit}`;

}



function MetricAnalysisPanel({ seriesKey, stats, onClose, statsLoading = false, fromServer = false }) {

  const meta = SERIES_META[seriesKey];

  const series = SERIES.find((s) => s.key === seriesKey);

  if (!meta || !stats || !series) return null;



  const trend = stats.trend;

  const trendLabel = trend == null

    ? '—'

    : `${trend >= 0 ? '+' : ''}${trend.toFixed(1)}%`;

  const trendTone = trend == null

    ? 'text-gray-500'

    : trend > 5

      ? 'text-red-600'

      : trend < -5

        ? 'text-emerald-600'

        : 'text-gray-600';



  const statCards = [

    { label: 'Current', value: formatMetricValue(stats.current, meta.unit) },

    { label: 'Average', value: formatMetricValue(stats.avg, meta.unit) },

    { label: 'Minimum', value: formatMetricValue(stats.min, meta.unit) },

    { label: 'Maximum', value: formatMetricValue(stats.max, meta.unit) },

  ];



  return (

    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">

      <div className="flex items-start justify-between gap-3 mb-3">

        <div className="min-w-0">

          <div className="flex items-center gap-2">

            <span

              className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"

              style={{ backgroundColor: series.color }}

            />

            <h3 className="text-sm font-bold text-gray-800">{meta.title}</h3>

            {meta.derived && (

              <span className="text-[10px] uppercase tracking-wide text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">

                Derived

              </span>

            )}

          </div>

          <p className="text-[11px] text-gray-500 mt-0.5">{meta.description}</p>

          <p className="text-[10px] text-gray-400 mt-1">

            {stats.samples} samples · {stats.firstDate} → {stats.lastDate}

            {fromServer ? ' · full log history' : ' · chart window'}

            {statsLoading && ' · updating…'}

          </p>

        </div>

        <button

          type="button"

          onClick={onClose}

          className="p-1 rounded-md text-gray-400 hover:bg-white hover:text-gray-600 transition flex-shrink-0"

          title="Close analysis"

        >

          <FiX className="w-4 h-4" />

        </button>

      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">

        {statCards.map((card) => (

          <div key={card.label} className="bg-white rounded-md border border-gray-100 px-2.5 py-2">

            <p className="text-[10px] uppercase tracking-wide text-gray-400">{card.label}</p>

            <p className="text-sm font-bold text-gray-900">{card.value}</p>

          </div>

        ))}

      </div>

      <div className="flex items-center gap-2 text-xs">

        <FiTrendingUp className={`w-3.5 h-3.5 ${trendTone}`} />

        <span className="text-gray-500">Recent trend:</span>

        <span className={`font-semibold ${trendTone}`}>{trendLabel}</span>

        <span className="text-gray-400">(last 5 vs prior 5 samples)</span>

      </div>

    </div>

  );

}



function MetricsHistoryChartPlot({ chartData, selectedMetric, selectedMeta, selectedSeries }) {

  return (

    <ResponsiveContainer width="100%" height="100%">

      {selectedMetric ? (

        <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 25 }}>

          <defs>

            <linearGradient id={`metric-focus-${selectedMetric}`} x1="0" y1="0" x2="0" y2="1">

              <stop offset="0%" stopColor={selectedSeries?.color} stopOpacity={0.3} />

              <stop offset="100%" stopColor={selectedSeries?.color} stopOpacity={0} />

            </linearGradient>

          </defs>

          <XAxis dataKey="Date" tick={{ fontSize: 10, fill: '#6b7280' }} interval="preserveStartEnd" axisLine={false} tickLine={false} />

          <YAxis {...HISTORY_Y_AXIS_PROPS} domain={['auto', 'auto']} />

          <Tooltip

            {...HISTORY_TOOLTIP_PROPS}

            formatter={(value) => [

              formatMetricValue(parseFloat(value), selectedMeta?.unit || ''),

              selectedMeta?.title || selectedMetric,

            ]}

          />

          <Area

            type="monotone"

            dataKey={selectedMetric}

            name={selectedSeries?.name}

            stroke={selectedSeries?.color}

            fill={`url(#metric-focus-${selectedMetric})`}

            strokeWidth={3}

            dot={false}

            isAnimationActive={false}

            connectNulls

          />

          <Brush dataKey="Date" height={28} stroke="#d1d5db" tickFormatter={(t) => t} />

        </AreaChart>

      ) : (

        <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 25 }}>

          <XAxis dataKey="Date" tick={{ fontSize: 10, fill: '#6b7280' }} interval="preserveStartEnd" axisLine={false} tickLine={false} />

          <YAxis {...HISTORY_Y_AXIS_PROPS} />

          <Tooltip

            {...HISTORY_TOOLTIP_PROPS}

            formatter={(value, name) => {

              const key = SERIES.find((s) => s.name === name)?.key;

              const unit = key ? SERIES_META[key]?.unit || '' : '';

              return [formatMetricValue(parseFloat(value), unit), name];

            }}

          />

          {SERIES.map((s) => (

            <Line

              key={s.key}

              type="monotone"

              dataKey={s.key}

              name={s.name}

              stroke={s.color}

              strokeWidth={2}

              dot={false}

              isAnimationActive={false}

            />

          ))}

          <Brush dataKey="Date" height={28} stroke="#d1d5db" tickFormatter={(t) => t} />

        </LineChart>

      )}

    </ResponsiveContainer>

  );

}



function MetricsHistoryChart({ history, loading, selectedServer = '', dataEnabled = true }) {

  const [selectedMetric, setSelectedMetric] = useState(null);

  const [serverStats, setServerStats] = useState(null);

  const [serverPoints, setServerPoints] = useState(null);

  const [statsLoading, setStatsLoading] = useState(false);

  const [apiHistory, setApiHistory] = useState([]);

  const [apiLoading, setApiLoading] = useState(false);

  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const mountedRef = useRef(true);

  const chartContainerRef = useRef(null);

  const fullscreenChartRef = useRef(null);



  const sourceHistory = dataEnabled ? (history?.length ? history : apiHistory) : [];



  const data = (sourceHistory || []).map((row) => ({

    ...row,

    CPULOAD: parseFloat(row.CPULOAD) || 0,

    MEMORYLOAD: parseFloat(row.MEMORYLOAD) || 0,

    PROCESSES: parseFloat(row.PROCESSES) || 0,

    DISKSIZE: parseFloat(row.DISKSIZE) || 0,

    REQUEST_LATENCY_MS: parseFloat(row.REQUEST_LATENCY_MS) || 0,

  }));



  const clientStats = selectedMetric ? computeMetricStats(data, selectedMetric) : null;

  const displayStats = serverStats ?? clientStats;

  const selectedMeta = selectedMetric ? SERIES_META[selectedMetric] : null;

  const selectedSeries = selectedMetric ? SERIES.find((s) => s.key === selectedMetric) : null;



  const chartData = useMemo(() => {

    if (!selectedMetric) return data;

    if (serverPoints?.length) {

      return serverPoints.map((p) => ({

        Date: p.Date,

        [selectedMetric]: parseFloat(p.value) || 0,

      }));

    }

    return data;

  }, [data, selectedMetric, serverPoints]);



  const hasChartPoints = chartData.length > 0;

  const chartLoading = loading || apiLoading;

  const serverLabel = selectedServer || 'all-servers';

  const historyTitle = selectedServer

    ? `Server Metric History - ${selectedServer}`

    : 'Server Metric History';

  const downloadFilename = buildChartFilename(serverLabel, 'metric-history');



  useEffect(() => {

    mountedRef.current = true;

    return () => { mountedRef.current = false; };

  }, []);



  useEffect(() => {

    setSelectedMetric(null);

    setServerStats(null);

    setServerPoints(null);

  }, [selectedServer]);



  useEffect(() => {

    if (!dataEnabled) {

      setApiHistory([]);

      setApiLoading(false);

      return undefined;

    }

    let cancelled = false;

    setApiLoading(true);



    (async () => {

      try {

        const hRes = await fetchStatusLogHistory(selectedServer);

        let series = parseHistoryResponse(hRes);

        if (series.length === 0) {

          const logRes = await fetchStatusLogs(selectedServer, 0, LOG_FETCH_SIZE, '', 0, 'desc');

          if (logRes.data?.data) {

            series = buildChartSeriesFromLogs(logRes.data.data, LOG_FETCH_SIZE);

          }

        }

        if (!cancelled) setApiHistory(series);

      } catch {

        if (!cancelled) setApiHistory([]);

      } finally {

        if (!cancelled) setApiLoading(false);

      }

    })();



    return () => { cancelled = true; };

  }, [selectedServer, history, dataEnabled]);



  useEffect(() => {

    if (!selectedMetric) {

      setServerStats(null);

      setServerPoints(null);

      setStatsLoading(false);

      return undefined;

    }



    let cancelled = false;

    setStatsLoading(true);



    fetchMetricStats(selectedServer, selectedMetric)

      .then((res) => {

        if (cancelled || !mountedRef.current) return;

        if (res.data?.stats) {

          setServerStats(res.data.stats);

          setServerPoints(Array.isArray(res.data.points) ? res.data.points : null);

        }

      })

      .catch(() => {

        if (!cancelled && mountedRef.current) {

          setServerStats(null);

          setServerPoints(null);

        }

      })

      .finally(() => {

        if (!cancelled && mountedRef.current) setStatsLoading(false);

      });



    return () => { cancelled = true; };

  }, [selectedMetric, selectedServer, history]);



  const handleLegendClick = (key) => {

    if (selectedMetric === key) {

      setSelectedMetric(null);

      return;

    }

    setServerStats(null);

    setServerPoints(null);

    setSelectedMetric(key);

  };



  return (

    <>

    <section className="server-metric-history-chart bg-white rounded-xl border border-gray-100 shadow-sm p-4 w-full">

      <div className="flex items-center justify-between mb-3 gap-2">

        <div className="min-w-0">

          <h2 className="text-sm font-bold text-gray-800">{historyTitle}</h2>

          <p className="text-[11px] text-gray-500">

            {selectedMetric

              ? `Focused on ${selectedMeta?.title || selectedMetric} · click legend again to show all metrics`

              : 'From status log table · Click a legend item to focus and analyze'}

          </p>

        </div>

        {hasChartPoints && (

          <ChartExportActions

            containerRef={chartContainerRef}

            filename={downloadFilename}

            onFullscreen={() => setFullscreenOpen(true)}

          />

        )}

      </div>



      {data.length > 0 && (

        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 mb-2">

          {SERIES.map((s) => {

            const active = selectedMetric === s.key;

            const dimmed = selectedMetric && !active;

            return (

              <button

                key={s.key}

                type="button"

                onClick={() => handleLegendClick(s.key)}

                className={`inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2 py-0.5 transition

                  ${active ? 'bg-gray-100 ring-1 ring-gray-300 text-gray-900' : 'text-gray-600 hover:bg-gray-50'}

                  ${dimmed ? 'opacity-40' : ''}`}

                title={`Analyze ${s.name}`}

              >

                <span

                  className="w-2 h-2 rounded-full flex-shrink-0"

                  style={{ backgroundColor: s.color }}

                />

                {s.name}

              </button>

            );

          })}

        </div>

      )}



      <div

        ref={chartContainerRef}

        className="w-full h-[380px] bg-white rounded-lg outline-none"

        onMouseDown={(e) => e.stopPropagation()}

        onPointerDown={(e) => e.stopPropagation()}

      >

        {chartLoading && !hasChartPoints ? (

          <div className="h-full flex items-center justify-center text-sm text-gray-400">Loading chart…</div>

        ) : !hasChartPoints ? (

          <div className="h-full flex items-center justify-center text-sm text-gray-400">

            {selectedServer

              ? `No status log history for ${selectedServer}`

              : 'No history for this server filter'}

          </div>

        ) : (

          <MetricsHistoryChartPlot

            chartData={chartData}

            selectedMetric={selectedMetric}

            selectedMeta={selectedMeta}

            selectedSeries={selectedSeries}

          />

        )}

      </div>



      {selectedMetric && displayStats && (

        <MetricAnalysisPanel

          seriesKey={selectedMetric}

          stats={displayStats}

          statsLoading={statsLoading}

          fromServer={Boolean(serverStats)}

          onClose={() => setSelectedMetric(null)}

        />

      )}

    </section>



    <ChartFullscreenModal

      open={fullscreenOpen}

      title={historyTitle}

      onClose={() => setFullscreenOpen(false)}

    >

      {hasChartPoints && (

        <div ref={fullscreenChartRef} className="w-full h-full min-h-[70vh]">

          <MetricsHistoryChartPlot

            chartData={chartData}

            selectedMetric={selectedMetric}

            selectedMeta={selectedMeta}

            selectedSeries={selectedSeries}

          />

        </div>

      )}

    </ChartFullscreenModal>

    </>

  );

}







const PAGE_SIZES = [10, 25, 50, 100, 500];



const LIVE_POLL_MS = 10000;



function StatusLogTable({ selectedServer, refreshKey, onChartData, liveEnabled }) {

  const [logs, setLogs] = useState([]);

  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(0);

  const [pageSize, setPageSize] = useState(10);

  const [search, setSearch] = useState('');

  const [sortCol, setSortCol] = useState(0);

  const [sortDir, setSortDir] = useState('desc');

  const [loading, setLoading] = useState(false);

  const [lastRefresh, setLastRefresh] = useState(null);



  const loadCharts = useCallback(async () => {

    try {

      const chartRes = await fetchStatusLogs(selectedServer, 0, LOG_FETCH_SIZE, '', 0, 'desc');

      if (!onChartData) return;

      const chartRows = chartRes.data?.data

        ? chartRes.data.data.map(normalizeLogRow)

        : [];

      onChartData(buildChartSeriesFromLogs(chartRows, LOG_FETCH_SIZE));

    } catch {

      if (onChartData) onChartData([]);

    }

  }, [selectedServer, refreshKey, onChartData]);



  const loadTable = useCallback(async (silent = false) => {

    if (!silent) setLoading(true);

    try {

      const tableRes = await fetchStatusLogs(

        selectedServer,

        page * pageSize,

        pageSize,

        search.trim(),

        sortCol,

        sortDir,

      );

      if (tableRes.data?.data) {

        const rows = tableRes.data.data.map(normalizeLogRow);

        setLogs(rows);

        setTotal(tableRes.data.recordsFiltered ?? tableRes.data.recordsTotal ?? rows.length);

      } else {

        setLogs([]);

        setTotal(0);

      }

      setLastRefresh(new Date());

    } catch {

      setLogs([]);

      setTotal(0);

    } finally {

      if (!silent) setLoading(false);

    }

  }, [selectedServer, page, pageSize, search, sortCol, sortDir, refreshKey]);



  // Table filters — reset pagination only; charts stay unchanged

  useEffect(() => {

    setPage(0);

  }, [selectedServer, search, pageSize, sortCol, sortDir]);



  // Server filter changes chart dataset

  useEffect(() => {

    if (onChartData) onChartData([]);

    loadCharts();

  }, [selectedServer, refreshKey, onChartData, loadCharts]);



  useEffect(() => {

    loadTable();

  }, [loadTable]);



  // Live auto-refresh polling

  useEffect(() => {

    if (!liveEnabled) return undefined;

    const id = setInterval(() => {

      loadTable(true);

      loadCharts();

    }, LIVE_POLL_MS);

    return () => clearInterval(id);

  }, [liveEnabled, loadTable, loadCharts]);



  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const from = total === 0 ? 0 : page * pageSize + 1;

  const to = Math.min(total, (page + 1) * pageSize);



  const handleSort = (colIdx) => {

    if (sortCol === colIdx) {

      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');

    } else {

      setSortCol(colIdx);

      setSortDir('asc');

    }

  };



  const handleExportExcel = () => {

    const baseUrl = getBase();

    const token = localStorage.getItem('token') || '';

    const serverParam = selectedServer ? encodeURIComponent(selectedServer) : '';

    const downloadUrl = `${baseUrl}/download_server_log?servername=${serverParam}&token=${encodeURIComponent(token)}`;

    

    const link = document.createElement('a');

    link.href = downloadUrl;

    link.target = '_blank';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

  };



  const columns = [

    { label: 'S No.', idx: 0 },

    { label: 'SERVERNAME', idx: 1 },

    { label: 'TYPE', idx: 2 },

    { label: 'CPULOAD', idx: 3 },

    { label: 'MEMORYLOAD', idx: 4 },

    { label: 'PROCESSES', idx: 5 },

    { label: 'DISKSIZE', idx: 6 },

    { label: 'REPORTING DATE TIME', idx: 7 },

  ];



  return (

    <section className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden w-full">

      <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">

        <div className="flex items-center gap-3">

          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Status Log Records</h2>

          {liveEnabled && (

            <span className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">

              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />

              Live · {lastRefresh ? lastRefresh.toLocaleTimeString() : '...'}

            </span>

          )}

        </div>

        <div className="flex flex-wrap items-center gap-3">

          <label className="flex items-center gap-2 text-xs text-gray-600">

            Show

            <select

              value={pageSize}

              onChange={(e) => setPageSize(Number(e.target.value))}

              className="border border-gray-200 rounded px-2 py-1 text-xs bg-white"

            >

              {PAGE_SIZES.map((n) => (

                <option key={n} value={n}>{n}</option>

              ))}

            </select>

            entries

          </label>

          <div className="relative">

            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

            <input

              type="text" placeholder="Search logs…" value={search}

              onChange={(e) => setSearch(e.target.value)}

              className="pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-600 w-44"

            />

          </div>

          <button

            type="button"

            onClick={handleExportExcel}

            title="Download Excel Report"

            className="flex items-center justify-center p-1.5 text-white bg-green-600 hover:bg-green-700 rounded-lg transition shadow-sm w-8 h-8"

          >

            <FiDownload className="w-4 h-4" />

          </button>

        </div>

      </div>



      <div className="overflow-x-auto overflow-y-auto w-full max-h-[500px]">

        <table className="w-full text-left text-xs border-collapse min-w-[720px] relative">

          <thead className="sticky top-0 z-10 shadow-sm bg-gray-50">

            <tr className="border-b border-gray-200 text-gray-700 font-semibold uppercase text-[10px] tracking-wider">

              {columns.map((col) => (

                <th

                  key={col.idx}

                  onClick={() => handleSort(col.idx)}

                  className="px-3 py-3 border-r border-gray-200 last:border-r-0 whitespace-nowrap cursor-pointer hover:bg-gray-100 transition select-none"

                >

                  <div className="flex items-center gap-1">

                    {col.label}

                    <span className="flex flex-col opacity-50">

                      <FiChevronUp className={`w-2.5 h-2.5 -mb-1 ${sortCol === col.idx && sortDir === 'asc' ? 'text-red-600 opacity-100' : ''}`} />

                      <FiChevronDown className={`w-2.5 h-2.5 ${sortCol === col.idx && sortDir === 'desc' ? 'text-red-600 opacity-100' : ''}`} />

                    </span>

                  </div>

                </th>

              ))}

            </tr>

          </thead>

          <tbody className="divide-y divide-gray-100 bg-white">

            {loading ? (

              <tr><td colSpan={8} className="p-8 text-center text-gray-400">Loading…</td></tr>

            ) : logs.length === 0 ? (

              <tr><td colSpan={8} className="p-8 text-center text-gray-400">No log records found</td></tr>

            ) : logs.map((log, i) => (

              <tr key={`${log.REPORTINGDATETIME}-${i}`} className="hover:bg-neutral-50">

                <td className="px-3 py-2 border-r border-gray-50">{page * pageSize + i + 1}</td>

                <td className="px-3 py-2 font-semibold text-gray-800 border-r border-gray-50">{log.SERVERNAME}</td>

                <td className="px-3 py-2 border-r border-gray-50">{log.TYPE}</td>

                <td className="px-3 py-2 font-bold text-red-700 border-r border-gray-50">{parseFloat(log.CPULOAD).toFixed(1)}%</td>

                <td className="px-3 py-2 font-bold text-emerald-700 border-r border-gray-50">{parseFloat(log.MEMORYLOAD).toFixed(1)}%</td>

                <td className="px-3 py-2 font-bold text-rose-600 border-r border-gray-50">{log.PROCESSES}</td>

                <td className="px-3 py-2 font-bold text-amber-600 border-r border-gray-50">{log.DISKSIZE}</td>

                <td className="px-3 py-2 font-mono text-gray-600">{log.REPORTINGDATETIME}</td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>



      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">

        <div className="text-sm text-gray-600 order-2 sm:order-1">

          Showing <span className="font-semibold">{from}</span>-<span className="font-semibold">{to}</span> of <span className="font-semibold">{total}</span>

        </div>

        <div className="flex gap-1 sm:gap-2 items-center order-1 sm:order-2">

          <button

            type="button"

            onClick={() => setPage(0)}

            disabled={page === 0}

            className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center font-medium"

          >

            <span className="hidden sm:inline">First</span>

            <span className="sm:hidden text-lg">«</span>

          </button>



          {(() => {

            const pages = [];

            const isMobile = window.innerWidth < 640;

            const maxVisible = isMobile ? 3 : 5;

            const currentPage = page + 1;

            

            let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));

            let endPage = Math.min(totalPages, startPage + maxVisible - 1);



            if (endPage - startPage + 1 < maxVisible) {

              startPage = Math.max(1, endPage - maxVisible + 1);

            }

            startPage = Math.max(1, startPage);



            for (let i = startPage; i <= endPage; i++) {

              pages.push(i);

            }



            return pages.map((pageNum) => (

              <button

                key={pageNum}

                type="button"

                onClick={() => setPage(pageNum - 1)}

                className={`min-w-[32px] h-[32px] flex items-center justify-center text-xs sm:text-sm border rounded font-medium transition-all ${currentPage === pageNum

                    ? "bg-red-600 text-white border-red-600 shadow-sm scale-110 z-10"

                    : "border-gray-300 hover:bg-gray-100 bg-white text-black hover:border-gray-400"

                  }`}

              >

                {pageNum}

              </button>

            ));

          })()}



          <button

            type="button"

            onClick={() => setPage(totalPages - 1)}

            disabled={page + 1 >= totalPages}

            className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center font-medium"

          >

            <span className="hidden sm:inline">Last</span>

            <span className="sm:hidden text-lg">»</span>

          </button>

        </div>

      </div>

    </section>

  );

}









// ─── Main ───



function loadJson(key, fallback) {

  try {

    const raw = localStorage.getItem(key);

    return raw ? JSON.parse(raw) : fallback;

  } catch {

    return fallback;

  }

}



const DEFAULT_LAYOUT_BY_ID = Object.fromEntries(DEFAULT_LAYOUT.map((l) => [l.i, l]));

const DEFAULT_WIDGET_IDS = new Set(DEFAULT_WIDGETS.map((w) => w.id));



function isDefaultWidgetSet(widgets) {

  if (widgets.length !== DEFAULT_WIDGETS.length) return false;

  const ids = new Set(widgets.map((w) => w.id));

  return DEFAULT_WIDGETS.every((w) => ids.has(w.id));

}



/** Detect known broken layouts from old storage (narrow columns, stacked rows). User drag positions are kept. */

function isBrokenLegacyLayout(widgets, layout) {

  if (!isDefaultWidgetSet(widgets) || !layout?.length) return false;

  const map = Object.fromEntries(layout.map((l) => [l.i, l]));

  const required = ['cpu', 'memory', 'latency', 'disk', 'processes'];

  if (required.some((id) => !map[id])) return true;

  if (required.some((id) => (map[id].w ?? 0) < 1 || (map[id].h ?? 0) < 1)) return true;

  const disk = map.disk;

  const processes = map.processes;

  // Old bug: disk and processes stacked in the same column

  if (disk.x === processes.x) return true;

  const topIds = ['cpu', 'memory', 'latency'];

  const topY = map.cpu.y;

  const topSameRow = topIds.every((id) => map[id].y === topY);

  if (topSameRow) {

    const topEnd = Math.max(...topIds.map((id) => map[id].x + map[id].w));

    const allNarrow = topIds.every((id) => map[id].w <= 2);

    // Old bug: three narrow charts clustered on the left (span ≤ 6)

    if (allNarrow && topEnd <= 6) return true;

  }

  const bottomSameRow = disk.y === processes.y;

  if (bottomSameRow) {

    const bottomEnd = Math.max(disk.x + disk.w, processes.x + processes.w);

    const bothNarrow = disk.w <= 3 && processes.w <= 3;

    if (bothNarrow && bottomEnd <= 6) return true;

  }

  return false;

}



/**

 * Keep grid cells aligned with widgets. Preserves user drag/resize positions

 * from localStorage — only adds missing widget slots.

 */

function syncLayoutWithWidgets(widgets, layout) {

  const widgetIds = new Set(widgets.map((w) => w.id));

  const synced = (layout || []).filter((l) => widgetIds.has(l.i));

  const layoutIds = new Set(synced.map((l) => l.i));

  let maxY = synced.reduce((m, l) => Math.max(m, l.y + l.h), 0);



  widgets.forEach((w) => {

    if (!layoutIds.has(w.id)) {

      const def = DEFAULT_LAYOUT_BY_ID[w.id];

      if (def) {

        synced.push({ ...def });

      } else {

        synced.push({ i: w.id, x: 0, y: maxY, w: 4, h: 2, minW: 3, minH: 2 });

        maxY += 2;

      }

    }

  });



  return synced;

}



function loadWidgetsWithDefaults() {

  const saved = loadJson(WIDGETS_STORAGE_KEY, null);

  const valid = (items) => items.filter((w) => METRIC_DEFS[w.metricId]);

  if (!saved?.length) return DEFAULT_WIDGETS;

  const ids = new Set(saved.map((w) => w.metricId));

  const merged = [...saved];

  DEFAULT_WIDGETS.forEach((dw) => {

    if (!ids.has(dw.metricId)) merged.push(dw);

  });

  return valid(merged);

}



function loadLayoutWithDefaults() {

  const widgets = loadWidgetsWithDefaults();

  const saved = loadJson(LAYOUT_STORAGE_KEY, null);

  let base = saved?.length ? saved : DEFAULT_LAYOUT;

  if (isBrokenLegacyLayout(widgets, base)) {

    base = DEFAULT_LAYOUT;

  }

  const layout = syncLayoutWithWidgets(widgets, base);

  if (isBrokenLegacyLayout(widgets, layout)) {

    return syncLayoutWithWidgets(widgets, DEFAULT_LAYOUT);

  }

  return layout;

}



function KpiCard({ label, value, unit, trend, icon: Icon, warn }) {

  const trendNum = trend != null ? trend : null;

  const up = trendNum != null && trendNum > 0;

  const trendLabel =

    trendNum == null

      ? null

      : `${up ? '↑' : '↓'} ${Math.abs(trendNum).toFixed(1)}% vs last 5m`;



  return (

    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col justify-between min-h-[100px]">

      <div className="flex items-start justify-between">

        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{label}</span>

        <Icon className={`w-4 h-4 ${warn ? 'text-red-400' : 'text-gray-300'}`} />

      </div>

      <div>

        <p className="text-2xl font-bold text-gray-900 mt-1">

          {typeof value === 'number' && Number.isFinite(value)

            ? `${value.toFixed(label === 'ERRORS' ? 1 : 1)}${unit}`

            : value}

        </p>

        {trendLabel && (

          <p className={`text-[11px] mt-0.5 font-medium ${up && !warn ? 'text-red-600' : 'text-emerald-600'}`}>

            {trendLabel}

          </p>

        )}

      </div>

    </div>

  );

}



function InstanceCard({ server, health, selected, onSelect, style }) {

  const sub = server.logOnly

    ? 'Status logs · not in inventory JSON'

    : [server.os_version || server.os_type, server.private_ip].filter(Boolean).join(' - ') || 'Monitored node';

  return (

    <button

      type="button"

      onClick={() => onSelect(server.server_name)}

      className={`text-left w-full bg-white rounded-xl border p-3 transition shadow-sm hover:shadow-md

        ${selected ? style.border + ' ring-2 ring-red-100' : 'border-gray-100 hover:border-gray-200'}`}

    >

      <div className="flex items-start justify-between gap-2 mb-2">

        <div className="min-w-0">

          <p className="font-mono text-xs font-bold text-gray-800 truncate" title={server.server_name}>

            {server.server_name}

          </p>

          <p className="text-[10px] text-gray-500 truncate mt-0.5">{sub}</p>

        </div>

        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${style.badge}`}>

          {health.label}

        </span>

      </div>

      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">

        <div

          className={`h-full rounded-full transition-all duration-500 ${style.bar}`}

          style={{ width: `${Math.min(100, Math.max(4, health.pct))}%` }}

        />

      </div>

    </button>

  );

}



const EDC_TABS = [

  { id: 'operations', label: 'Server Operations Dashboard', icon: FiActivity },

  { id: 'infrastructure', label: 'Server Infrastructure', icon: FiServer },

];



function OperationsDashboard({ onClose, visible = true }) {

  const [selectedServer, setSelectedServer] = useState('');

  const [liveEnabled, setLiveEnabled] = useState(true);

  const [layout, setLayout] = useState(() => loadLayoutWithDefaults());

  const [widgets, setWidgets] = useState(() => loadWidgetsWithDefaults());



  const layoutHydratedRef = useRef(false);

  const isUndoingRef = useRef(false);

  const widgetsRef = useRef(widgets);

  const lastCommittedLayoutRef = useRef(layout);



  useEffect(() => {

    widgetsRef.current = widgets;

  }, [widgets]);



  useEffect(() => {

    const id = requestAnimationFrame(() => {

      layoutHydratedRef.current = true;

      setLayout((current) => {

        const widgets = widgetsRef.current;

        const synced = syncLayoutWithWidgets(widgets, current);

        if (!isBrokenLegacyLayout(widgets, synced)) {

          lastCommittedLayoutRef.current = synced;

          return synced;

        }

        const fixed = syncLayoutWithWidgets(widgets, DEFAULT_LAYOUT);

        lastCommittedLayoutRef.current = fixed;

        localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(fixed));

        return fixed;

      });

    });

    return () => cancelAnimationFrame(id);

  }, []);



  const [preview, setPreview] = useState(null);

  const [logByName, setLogByName] = useState({});

  const gridWrapRef = useRef(null);

  const [gridWidth, setGridWidth] = useState(900);

  const [showAllCards, setShowAllCards] = useState(false);

  const [showActionDropdown, setShowActionDropdown] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [showTruncateModal, setShowTruncateModal] = useState(false);

  // Undo history: persisted to sessionStorage so it survives page refresh but clears on logout

  const [undoStack, setUndoStack] = useState(() => {

    try {

      const saved = sessionStorage.getItem(UNDO_STACK_SESSION_KEY);

      return saved ? JSON.parse(saved) : [];

    } catch { return []; }

  });



  // Chart data driven by StatusLogTable

  const [tableHistory, setTableHistory] = useState([]);

  const handleChartData = useCallback((series) => {

    setTableHistory(Array.isArray(series) ? series : []);

  }, []);



  const selectServer = useCallback((name) => {

    setTableHistory([]);

    setFetchedHistory([]);

    setSelectedServer(name);

  }, []);



  // Server list + sync reports (no chart series)

  const {

    servers, syncFailed, loading, error, lastUpdated, refresh,

  } = useMetricsData(selectedServer, false, 'demo'); // polling disabled, table handles it



  const [fetchedHistory, setFetchedHistory] = useState([]);



  useEffect(() => {

    let cancelled = false;

    (async () => {

      try {

        const hRes = await fetchStatusLogHistory(selectedServer);

        let series = parseHistoryResponse(hRes);

        if (series.length === 0) {

          const logRes = await fetchStatusLogs(selectedServer, 0, LOG_FETCH_SIZE, '', 0, 'desc');

          if (logRes.data?.data) {

            series = buildChartSeriesFromLogs(logRes.data.data, LOG_FETCH_SIZE);

          }

        }

        if (!cancelled) setFetchedHistory(series);

      } catch {

        if (!cancelled) setFetchedHistory([]);

      }

    })();

    return () => { cancelled = true; };

  }, [selectedServer, lastUpdated]);



  // Prefer table-driven series when available; fall back to direct API fetch

  const history = tableHistory.length > 0 ? tableHistory : fetchedHistory;

  const selectedHasLogs = !selectedServer || Boolean(logByName[selectedServer]);

  const displayHistory = selectedHasLogs ? history : [];

  const showMetrics = !selectedServer ? displayHistory.length > 0 : selectedHasLogs;



  useEffect(() => {

    const el = gridWrapRef.current;

    if (!el || !showMetrics || !visible) return undefined;

    const measure = () => {

      const w = Math.floor(el.getBoundingClientRect().width);

      if (w > 0) setGridWidth(w);

    };

    const ro = new ResizeObserver(() => measure());

    ro.observe(el);

    measure();

    window.addEventListener('resize', measure);

    const t = window.setTimeout(measure, 150);

    return () => {

      ro.disconnect();

      window.removeEventListener('resize', measure);

      window.clearTimeout(t);

    };

  }, [showMetrics, visible]);



  useEffect(() => {

    let cancelled = false;

    (async () => {

      try {

        const res = await fetchLogServersSnapshot();

        if (cancelled || !res.data?.data) return;

        const map = {};

        res.data.data.forEach((item) => {

          const row = normalizeLogRow(item);

          const name = row.SERVERNAME;

          if (name) map[name] = row;

        });

        setLogByName(map);

      } catch { /* optional enrichment */ }

    })();

    return () => { cancelled = true; };

  }, [lastUpdated, tableHistory]);



  const allServers = useMemo(

    () => mergeInventoryWithLogServers(servers, logByName),

    [servers, logByName],

  );



  const displayWidgets = preview?.widgets ?? widgets;

  const displayLayout = useMemo(

    () => syncLayoutWithWidgets(displayWidgets, preview?.layout ?? layout),

    [displayWidgets, preview?.layout, layout],

  );



  const kpi = useMemo(

    () => aggregateKpis(displayHistory, syncFailed),

    [displayHistory, syncFailed],

  );







  const serverNames = useMemo(

    () => [...new Set(allServers.map((s) => s.server_name).filter(Boolean))].sort(),

    [allServers],

  );



  const memoryDisplay = Math.min(100, kpi.memory.value ?? 0);



  const commitLayout = useCallback((newLayout) => {

    if (!layoutHydratedRef.current || isUndoingRef.current) return;

    const normalized = syncLayoutWithWidgets(widgetsRef.current, newLayout);

    const previous = lastCommittedLayoutRef.current;



    if (JSON.stringify(normalized) !== JSON.stringify(previous)) {

      const w = widgetsRef.current;

      setUndoStack((stack) => {

        const next = [...stack.slice(-19), { layout: previous, widgets: w }];

        try { sessionStorage.setItem(UNDO_STACK_SESSION_KEY, JSON.stringify(next)); } catch {}

        return next;

      });

    }



    lastCommittedLayoutRef.current = normalized;

    setLayout(normalized);

    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(normalized));

  }, []);



  const onLayoutChange = useCallback((newLayout) => {

    if (preview?.active) {

      setPreview((p) => ({ ...p, layout: newLayout, active: true }));

      return;

    }

    if (!layoutHydratedRef.current || isUndoingRef.current) return;

    setLayout(syncLayoutWithWidgets(widgetsRef.current, newLayout));

  }, [preview?.active]);



  const persistWidgets = useCallback((w) => {

    if (JSON.stringify(w) === JSON.stringify(widgetsRef.current)) return;

    setUndoStack((stack) => {

      const next = [...stack.slice(-19), {

        layout: lastCommittedLayoutRef.current,

        widgets: widgetsRef.current,

      }];

      try { sessionStorage.setItem(UNDO_STACK_SESSION_KEY, JSON.stringify(next)); } catch {}

      return next;

    });

    widgetsRef.current = w;

    setWidgets(w);

    localStorage.setItem(WIDGETS_STORAGE_KEY, JSON.stringify(w));

  }, []);



  // Undo: restore last snapshot from the stack and sync sessionStorage

  const undo = useCallback(() => {

    setUndoStack((stack) => {

      if (stack.length === 0) return stack;



      const snapshot = stack[stack.length - 1];

      const next = stack.slice(0, -1);



      isUndoingRef.current = true;

      const restoredLayout = syncLayoutWithWidgets(snapshot.widgets, snapshot.layout);

      lastCommittedLayoutRef.current = restoredLayout;

      setLayout(restoredLayout);

      setWidgets(snapshot.widgets);

      widgetsRef.current = snapshot.widgets;

      localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(restoredLayout));

      localStorage.setItem(WIDGETS_STORAGE_KEY, JSON.stringify(snapshot.widgets));

      try { sessionStorage.setItem(UNDO_STACK_SESSION_KEY, JSON.stringify(next)); } catch {}



      requestAnimationFrame(() => {

        isUndoingRef.current = false;

      });



      return next;

    });

  }, []);



  // Ctrl+Z keyboard shortcut for undo

  useEffect(() => {

    const handler = (e) => {

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {

        e.preventDefault();

        undo();

      }

    };

    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);

  }, [undo]);



  // Clear undo stack ONLY on logout via the custom 'app:logout' event from logout.js.

  // Page refreshes preserve the undo stack via sessionStorage.

  useEffect(() => {

    const handleLogout = () => {

      try { sessionStorage.removeItem(UNDO_STACK_SESSION_KEY); } catch {}

      setUndoStack([]);

    };

    window.addEventListener('app:logout', handleLogout);

    return () => window.removeEventListener('app:logout', handleLogout);

  }, []);



  const applyDashboardAction = useCallback((cmd, opts = {}) => {

    const baseWidgets = opts.preview ? (preview?.widgets ?? widgets) : widgets;

    const baseLayout = opts.preview ? (preview?.layout ?? layout) : layout;



    if (cmd.type === 'add_chart') {

      if (baseWidgets.some((w) => w.metricId === cmd.metricId)) {

        return { ok: false, message: `Chart for ${cmd.metricId} is already on the dashboard.` };

      }

      const def = METRIC_DEFS[cmd.metricId];

      const newWidgets = [...baseWidgets, { id: cmd.metricId, metricId: cmd.metricId, chartType: def.defaultType }];

      const maxY = baseLayout.reduce((m, item) => Math.max(m, item.y + item.h), 0);

      const newLayout = [

        ...baseLayout,

        { i: cmd.metricId, x: 0, y: maxY, w: 6, h: 2, minW: 3, minH: 2 },

      ];

      if (opts.preview) {

        setPreview({ layout: newLayout, widgets: newWidgets, active: true });

      } else {

        persistWidgets(newWidgets);

        commitLayout(newLayout);

      }

      return { ok: true, message: `Added ${def.title} chart to preview.` };

    }



    if (cmd.type === 'remove_chart') {

      const newWidgets = baseWidgets.filter((w) => w.metricId !== cmd.metricId);

      const newLayout = baseLayout.filter((l) => l.i !== cmd.metricId);

      if (newWidgets.length === baseWidgets.length) {

        return { ok: false, message: 'That chart is not on the dashboard.' };

      }

      if (opts.preview) {

        setPreview({ layout: newLayout, widgets: newWidgets, active: true });

      } else {

        persistWidgets(newWidgets);

        commitLayout(newLayout);

      }

      return { ok: true, message: `Removed ${cmd.metricId} chart from preview.` };

    }



    if (cmd.type === 'set_chart_type') {

      const newWidgets = baseWidgets.map((w) =>

        w.metricId === cmd.metricId ? { ...w, chartType: cmd.chartType } : w,

      );

      if (opts.preview) {

        setPreview({ layout: baseLayout, widgets: newWidgets, active: true });

      } else {

        persistWidgets(newWidgets);

      }

      return { ok: true, message: `Set ${cmd.metricId} to ${cmd.chartType} chart in preview.` };

    }



    return { ok: false, message: 'Unknown dashboard command.' };

  }, [widgets, layout, preview, persistWidgets, commitLayout]);



  const onLayoutCommit = useCallback((newLayout) => {

    if (preview?.active) {

      setPreview((p) => ({ ...p, layout: newLayout, active: true }));

      return;

    }

    commitLayout(newLayout);

  }, [preview?.active, commitLayout]);



  const onTypeChange = (widgetId, chartType) => {

    const updater = (list) =>

      list.map((w) => (w.id === widgetId ? { ...w, chartType } : w));

    if (preview?.active) {

      setPreview((p) => ({ ...p, widgets: updater(p.widgets), active: true }));

    } else {

      persistWidgets(updater(widgets));

    }

  };



  const onRemove = (widgetId) => {

    const newWidgets = (preview?.widgets ?? widgets).filter((w) => w.id !== widgetId);

    const newLayout = (preview?.layout ?? layout).filter((l) => l.i !== widgetId);

    if (preview?.active) {

      setPreview({ layout: newLayout, widgets: newWidgets, active: true });

    } else {

      persistWidgets(newWidgets);

      commitLayout(newLayout);

    }

  };



  const applyPreview = () => {

    if (!preview) return;

    commitLayout(preview.layout);

    persistWidgets(preview.widgets);

    setPreview(null);

  };



  const discardPreview = () => setPreview(null);



  const sortedServers = [...allServers].sort((a, b) => {

    if (selectedServer) {

      if (a.server_name === selectedServer) return -1;

      if (b.server_name === selectedServer) return 1;

    }

    const loadA = getServerLoad(a, logByName);

    const healthA = healthFromLoad(loadA.cpu, loadA.mem, a.status, loadA.hasLog);

    const loadB = getServerLoad(b, logByName);

    const healthB = healthFromLoad(loadB.cpu, loadB.mem, b.status, loadB.hasLog);

    const toneRank = { 'red': 1, 'orange': 2, 'green': 3, 'gray': 4 };

    if (toneRank[healthA.tone] !== toneRank[healthB.tone]) {

      return toneRank[healthA.tone] - toneRank[healthB.tone];

    }

    return healthB.pct - healthA.pct;

  });



  const visibleCount = showAllCards ? sortedServers.length : 8; // Show top 8 by default

  const instanceList = sortedServers.slice(0, visibleCount);

  const hiddenCount = sortedServers.length - visibleCount;



  return (

    <div className="min-h-full w-full bg-[#f8f9fb] font-sans flex flex-col">

      <div className="w-full px-3 sm:px-4 py-4 space-y-4 flex-1">

        {/* Header */}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">

          <div className="flex items-start gap-3">

            <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center text-white shadow-sm">

              <FiActivity className="w-5 h-5" />

            </div>

            <div>

              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Server Operations Dashboard</h1>

              <p className="text-xs text-gray-500 mt-0.5">

                Live tracking · Charts driven by Status Log Records · {allServers.length} instance{allServers.length !== 1 ? 's' : ''}

              </p>

            </div>

          </div>

          <div className="flex flex-wrap items-center gap-2">

            <select

              value={selectedServer}

              onChange={(e) => selectServer(e.target.value)}

              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white min-w-[180px] focus:outline-none focus:ring-2 focus:ring-red-500/30"

            >

              <option value="">All Servers (Combined)</option>

              {serverNames.map((n) => (

                <option key={n} value={n}>{n}</option>

              ))}

            </select>

            <button

              type="button"

              onClick={() => setLiveEnabled((v) => !v)}

              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-gray-200 bg-white rounded-lg hover:bg-gray-50 transition"

            >

              {liveEnabled ? <FiPause className="w-4 h-4" /> : <FiPlay className="w-4 h-4" />}

              {liveEnabled ? 'Pause stream' : 'Resume stream'}

            </button>

            <span className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border

              ${liveEnabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>

              <span className={`w-2 h-2 rounded-full ${liveEnabled ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />

              {liveEnabled ? 'Live' : 'Paused'}

            </span>

            <button type="button" onClick={refresh} disabled={loading}

              className="flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition text-gray-600"

              title="Refresh data">

              <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />

            </button>

            <div className="relative">

              <button onClick={() => setShowActionDropdown(!showActionDropdown)}

                title="Manage Logs"

                className="flex items-center justify-center w-9 h-9 text-white bg-[#d32f2f] hover:bg-[#b71c1c] rounded-lg transition shadow-sm">

                <FiTrash2 className="w-4 h-4" />

              </button>

              {showActionDropdown && (

                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-[100] border border-gray-100">

                  <button onClick={() => { setShowDeleteModal(true); setShowActionDropdown(false); }}

                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">

                    <FiTrash2 className="text-red-500 w-4 h-4" /> Delete Server Logs

                  </button>

                  <button onClick={() => { setShowTruncateModal(true); setShowActionDropdown(false); }}

                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">

                    <FiCalendar className="text-orange-500 w-4 h-4" /> Truncate Logs by Date

                  </button>

                </div>

              )}

            </div>

            {onClose && (

              <button type="button" onClick={onClose}

                className="flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white hover:bg-red-50 hover:text-red-600 text-gray-600 transition"

                title="Close Dashboard">

                <FiX className="w-5 h-5" />

              </button>

            )}

          </div>

        </div>



        {error && (

          <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-2">

            {error}

          </div>

        )}



        <div className="w-full space-y-4">

            {/* Instance row */}

            {instanceList.length > 0 && (

              <section>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-3">

                  {instanceList.map((srv) => {

                    const load = getServerLoad(srv, logByName);

                    const health = healthFromLoad(load.cpu, load.mem, srv.status, load.hasLog);

                    const st = toneStyle(health.tone);

                    return (

                      <InstanceCard

                        key={srv.id}

                        server={srv}

                        health={health}

                        selected={selectedServer === srv.server_name}

                        onSelect={(name) => selectServer(selectedServer === name ? '' : name)}

                        style={st}

                      />

                    );

                  })}

                </div>

                <div className="mt-3 flex items-center gap-3">

                  {hiddenCount > 0 && (

                    <button

                      type="button"

                      onClick={() => setShowAllCards(true)}

                      className="text-xs px-4 py-1.5 rounded-lg border border-gray-200 bg-white font-semibold text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition shadow-sm"

                    >

                      Show {hiddenCount} more servers

                    </button>

                  )}

                  {showAllCards && allServers.length > 8 && (

                    <button

                      type="button"

                      onClick={() => setShowAllCards(false)}

                      className="text-xs px-4 py-1.5 rounded-lg border border-gray-200 bg-white font-semibold text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition shadow-sm"

                    >

                      Show less

                    </button>

                  )}

                  {selectedServer && (

                    <button

                      type="button"

                      onClick={() => selectServer('')}

                      className="text-xs text-red-600 font-semibold hover:underline"

                    >

                      Clear filter · show all servers

                    </button>

                  )}

                </div>

              </section>

            )}



            {/* KPI + charts — hidden when selected server has no logs */}

            {selectedServer && !selectedHasLogs ? (

              <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">

                <p className="text-sm font-semibold text-gray-800">No log records for {selectedServer}</p>

                <p className="text-xs text-gray-500 mt-1">Charts and metrics are hidden until status logs are available for this server.</p>

              </section>

            ) : showMetrics && (

              <>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">

              <KpiCard label="CPU" value={kpi.cpu.value} unit="%" trend={kpi.cpu.trend} icon={FiCpu} />

              <KpiCard label="MEMORY" value={memoryDisplay} unit="%" trend={kpi.memory.trend} icon={FiHardDrive} />

              <KpiCard label="PROCESSES" value={kpi.processes.value} unit="" trend={kpi.processes.trend} icon={FiActivity} />

              <KpiCard label="SYNC FAILURES" value={kpi.errors.value} unit="" trend={kpi.errors.trend} icon={FiAlertTriangle} warn />

            </div>



            <MetricsHistoryChart

              history={displayHistory}

              loading={loading}

              selectedServer={selectedServer}

              dataEnabled={selectedHasLogs || !selectedServer}

            />



            {/* Chart grid — data driven by Status Log Records */}

            <section className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4 w-full">

              <div className="flex items-start justify-between mb-3 px-1 gap-2">

                <div>

                  <h2 className="text-sm font-bold text-gray-800 flex items-center gap-2 flex-wrap">

                    {selectedServer ? `${selectedServer} Live Metrics` : 'Live Metrics'}

                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">

                      Driven by Status Log Records

                    </span>

                  </h2>

                  <p className="text-[11px] text-gray-500">

                    Charts follow the server filter only — table search does not affect them. Drag · resize · switch chart type.

                  </p>

                </div>

                <button

                  type="button"

                  onClick={undo}

                  disabled={undoStack.length === 0}

                  title={undoStack.length > 0

                    ? `Undo last layout change (Ctrl+Z) — ${undoStack.length} step${undoStack.length !== 1 ? 's' : ''} available`

                    : 'Nothing to undo'}

                  className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition

                    ${undoStack.length > 0

                      ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'

                      : 'border-gray-200 bg-white text-gray-300 cursor-not-allowed'}`}

                >

                  <FiRotateCcw className="w-3.5 h-3.5" />

                  <span>Undo</span>

                  {undoStack.length > 0 && (

                    <span className="bg-amber-200 text-amber-800 rounded-full px-1.5 text-[10px] font-bold leading-4">

                      {undoStack.length}

                    </span>

                  )}

                </button>

              </div>



              <div ref={gridWrapRef} className="relative w-full min-w-0">

                <GridLayout

                  className="layout"

                  layout={displayLayout}

                  cols={12}

                  rowHeight={GRID_ROW_HEIGHT}

                  width={gridWidth}

                  onLayoutChange={onLayoutChange}

                  onDragStop={onLayoutCommit}

                  onResizeStop={onLayoutCommit}

                  draggableHandle=".drag-handle"

                  compactType={null}

                  margin={[12, 12]}

                  useCSSTransforms={false}

                >

                  {displayWidgets.map((w) => (

                    <div key={w.id} style={{ height: '100%', width: '100%' }}>

                      <DashboardChartWidget

                        key={`${w.id}-${gridWidth}-${displayHistory.length}`}

                        widgetId={w.id}

                        metricId={w.metricId}

                        chartType={w.chartType}

                        history={displayHistory}

                        selectedServer={selectedServer}

                        onTypeChange={onTypeChange}

                        onRemove={onRemove}

                        isPreview={preview?.active}

                      />

                    </div>

                  ))}

                </GridLayout>

              </div>

            </section>

              </>

            )}



            {/* Status log table — at bottom, drives chart data */}

            <StatusLogTable

              selectedServer={selectedServer}

              refreshKey={lastUpdated?.getTime?.() ?? 0}

              onChartData={handleChartData}

              liveEnabled={liveEnabled}

            />

        </div>

        

        {/* Footer */}

        {/* */}

      </div>



      <DeleteServerModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} onSuccess={() => {}} />

      <TruncateLogModal open={showTruncateModal} onClose={() => setShowTruncateModal(false)} onSuccess={() => {}} />

    </div>

  );

}



function ServerMonitoringDashboard({ onClose }) {

  const location = useLocation();

  const navigate = useNavigate();



  useEffect(() => {

    if (location.pathname.includes('infrastructure-dashboard')) {

      navigate('/server-dashboard?tab=infrastructure', { replace: true });

    }

  }, [location.pathname, navigate]);



  const activeTab = useMemo(() => {

    if (location.pathname.includes('infrastructure-dashboard')) return 'infrastructure';

    const tab = new URLSearchParams(location.search).get('tab');

    return tab === 'infrastructure' ? 'infrastructure' : 'operations';

  }, [location.pathname, location.search]);



  const setActiveTab = (tabId) => {

    if (tabId === 'infrastructure') {

      navigate('/server-dashboard?tab=infrastructure', { replace: true });

      return;

    }

    navigate('/server-dashboard', { replace: true });

  };



  return (

    <div className="min-h-full w-full flex flex-col bg-[#f8f9fb]">

      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">

        <div className="px-3 sm:px-4 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">

          <div className="flex items-center gap-2 min-w-0">

            <div className="w-9 h-9 rounded-lg bg-red-600 flex items-center justify-center text-white shadow-sm flex-shrink-0">

              <FiServer className="w-4 h-4" />

            </div>

            <div className="min-w-0">

              <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">eDC Server Management</h1>

              <p className="text-[11px] text-gray-500 hidden sm:block">Operations monitoring and infrastructure inventory</p>

            </div>

          </div>

          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg overflow-x-auto max-w-full shadow-inner">

            {EDC_TABS.map((tab) => {

              const Icon = tab.icon;

              const active = activeTab === tab.id;

              return (

                <button

                  key={tab.id}

                  type="button"

                  onClick={() => setActiveTab(tab.id)}

                  className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-md whitespace-nowrap transition

                    ${active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}

                >

                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />

                  <span className="hidden md:inline">{tab.label}</span>

                  <span className="md:hidden">{tab.id === 'operations' ? 'Operations' : 'Infrastructure'}</span>

                </button>

              );

            })}

          </div>

        </div>

      </div>



      <div className={`flex-1 min-h-0 ${activeTab !== 'operations' ? 'hidden' : ''}`}>

        <OperationsDashboard onClose={onClose} visible={activeTab === 'operations'} />

      </div>

      <div className={`flex-1 min-h-0 ${activeTab !== 'infrastructure' ? 'hidden' : ''}`}>

        <InfrastructureDashboard onClose={onClose} />

      </div>

    </div>

  );

}



export default ServerMonitoringDashboard;

